import React, { useCallback, useState } from 'react';
import { Spin } from 'antd';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import { styled } from 'styled-components';
import { Button, GenericConfirmationModal, RowCentered } from '@ydistri/ds';

const ExceptionRow = styled(RowCentered)`
  justify-content: space-evenly;
  flex-grow: 1;
  flex-wrap: wrap;
`;

function getBorderVariant(
  isTemplate: boolean,
  isActive: boolean | undefined,
): 'none' | 'full' | 'transparent' {
  if (isTemplate) return 'none';
  return isActive ? 'full' : 'transparent';
}

interface TypeOfExceptionButtonsProps {
  allEnabled: boolean | undefined;
  status: ConfigurationFieldStatus;
  disabled: boolean;
  templateOrCalculationType: 'Template' | 'Calculation';
  hasException: boolean;
  onSetAllEnabled: () => void;
  onSetAllDisabled: () => void;
  confirmationTitle: string;
}

const TypeOfExceptionButtons = ({
  allEnabled,
  status,
  disabled,
  templateOrCalculationType,
  hasException,
  onSetAllEnabled,
  onSetAllDisabled,
  confirmationTitle,
}: TypeOfExceptionButtonsProps): React.ReactElement => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const toggleConfirmation = useCallback(() => setShowConfirmation(p => !p), []);

  const isTemplate = templateOrCalculationType === 'Template';

  const allEnabledHandler = useCallback(() => {
    if (allEnabled) return;
    if (hasException) {
      toggleConfirmation();
    } else {
      onSetAllEnabled();
    }
  }, [allEnabled, hasException, onSetAllEnabled, toggleConfirmation]);

  const allDisabledHandler = useCallback(() => {
    if (!allEnabled) return;
    if (hasException) {
      toggleConfirmation();
    } else {
      onSetAllDisabled();
    }
  }, [allEnabled, hasException, onSetAllDisabled, toggleConfirmation]);

  const handleConfirmed = useCallback(() => {
    if (allEnabled) {
      onSetAllDisabled();
    } else {
      onSetAllEnabled();
    }
    setShowConfirmation(false);
  }, [allEnabled, onSetAllDisabled, onSetAllEnabled]);

  const allEnabledBorderVariant = getBorderVariant(isTemplate, allEnabled);
  const allDisabledBorderVariant = getBorderVariant(isTemplate, !allEnabled);

  return (
    <ExceptionRow $gap="1rem">
      <Spin spinning={status === ConfigurationFieldStatus.LOADING}>
        <Button
          $colorVariant={allEnabled ? 'primary' : 'ghost'}
          $borderVariant={allEnabledBorderVariant}
          onClick={allEnabledHandler}
          $textVariant="semibold"
          disabled={disabled}
        >
          All enabled, except...
        </Button>
      </Spin>
      <Spin spinning={status === ConfigurationFieldStatus.LOADING}>
        <Button
          $colorVariant={allEnabled ? 'ghost' : 'primary'}
          $borderVariant={allDisabledBorderVariant}
          onClick={allDisabledHandler}
          $textVariant="semibold"
          disabled={disabled}
        >
          All disabled, except...
        </Button>
      </Spin>

      {showConfirmation && (
        <GenericConfirmationModal
          title={confirmationTitle}
          message={`You are about to change type of exception to "${
            allEnabled ? 'All disabled, except...' : 'All enabled, except...'
          }".
All exceptions will be cleared.`}
          confirmationButtonLabel="Clear all"
          onConfirmed={handleConfirmed}
          onCanceled={toggleConfirmation}
        />
      )}
    </ExceptionRow>
  );
};

export default TypeOfExceptionButtons;
